import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useForm } from 'react-hook-form';
import { checkEmailAllowed, checkExistingFranchisee, completeFranchiseeSignup } from '../services/signupService';
import { AuthLayout } from '../components/auth';
import { FormField, Button, FranchiseeDetailsForm } from '../components/forms';

const SignupPage = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(1); // 1: email/password, 2: franchisee details
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignupInProgress, setIsSignupInProgress] = useState(false); // Track if signup is in progress
  const navigate = useNavigate();

  // Form for email/password
  const { register, handleSubmit, formState: { errors } } = useForm();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);

      // Only redirect if user is authenticated AND signup is not in progress
      // This prevents redirect during the signup process
      if (currentUser && !isSignupInProgress) {
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate, isSignupInProgress]);

  const handleEmailPasswordSubmit = async (data) => {
    setError('');
    setIsSubmitting(true);
    setIsSignupInProgress(true); // Mark signup as in progress

    try {
      // First check if email is allowed
      await checkEmailAllowed(data.email);

      let firebaseUser;

      try {
        // Try to create Firebase user
        const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
        firebaseUser = userCredential.user;
      } catch (firebaseError) {
        // If user already exists, try to sign them in instead
        if (firebaseError.code === 'auth/email-already-in-use') {
          try {
            const signInCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
            firebaseUser = signInCredential.user;
            console.log('User already exists in Firebase, signed in instead');
          } catch (signInError) {
            throw new Error('An account with this email already exists. Please use the correct password or reset your password.');
          }
        } else {
          throw firebaseError;
        }
      }

      // Check if franchisee already exists
      const existingResult = await checkExistingFranchisee(data.email);

      console.log('Existing franchisee check result:', existingResult);

      if (existingResult.hasExistingFranchisee) {
        // Complete signup with existing franchisee
        await completeFranchiseeSignup(firebaseUser.uid, data.email);
        setIsSignupInProgress(false); // Mark signup as complete
        navigate('/');
      } else {
        // Need to collect franchisee details - keep signup in progress
        setStep(2);
      }
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
      console.error('Signup error:', err);
      setIsSignupInProgress(false); // Reset on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFranchiseeDetailsSubmit = async (data) => {
    setError('');
    setIsSubmitting(true);

    try {
      if (!user) {
        throw new Error('User authentication lost. Please try again.');
      }

      // Add email to franchisee data
      const franchiseeData = {
        ...data,
        email: user.email
      };

      // Complete signup with new franchisee data
      await completeFranchiseeSignup(user.uid, user.email, franchiseeData);
      setIsSignupInProgress(false); // Mark signup as complete
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to complete signup. Please try again.');
      console.error('Franchisee details error:', err);
      setIsSignupInProgress(false); // Reset on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setError('');
    // Keep isSignupInProgress as true - don't reset it when going back
    // This prevents redirect to home page
  };

  const handleCancelSignup = async () => {
    try {
      // Sign out the user and reset state
      if (user) {
        await auth.signOut();
      }
      setIsSignupInProgress(false);
      setStep(1);
      setError('');
      navigate('/login');
    } catch (err) {
      console.error('Error cancelling signup:', err);
      setError('Error cancelling signup. Please try again.');
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Loading..." subtitle="Please wait while we check your authentication status">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      </AuthLayout>
    );
  }

  const renderEmailPasswordForm = () => (
    <form onSubmit={handleSubmit(handleEmailPasswordSubmit)} className="space-y-6">
      <FormField
        label="Email Address"
        name="email"
        type="email"
        register={register}
        validation={{
          required: "Email is required",
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: "Please enter a valid email address"
          }
        }}
        error={errors.email}
        placeholder="your.email@example.com"
        required
      />

      <FormField
        label="Password"
        name="password"
        type="password"
        register={register}
        validation={{
          required: "Password is required",
          minLength: {
            value: 6,
            message: "Password must be at least 6 characters"
          }
        }}
        error={errors.password}
        placeholder="Choose a secure password"
        required
      />

      <Button
        type="submit"
        loading={isSubmitting}
        className="w-full"
      >
        {isSubmitting ? 'Creating Account...' : 'Create Account'}
      </Button>
    </form>
  );

  return (
    <AuthLayout
      title={step === 1 ? 'Create Your Account' : 'Complete Your Profile'}
      subtitle={step === 1 ? (
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-green-600 hover:text-green-500 underline">
            Sign in here
          </Link>
        </>
      ) : (
        'Please provide your franchisee details to complete registration'
      )}
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
          <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {step === 1 ? (
        renderEmailPasswordForm()
      ) : (
        <FranchiseeDetailsForm
          onSubmit={handleFranchiseeDetailsSubmit}
          isSubmitting={isSubmitting}
          onBack={handleBackToStep1}
          onCancel={handleCancelSignup}
        />
      )}
    </AuthLayout>
  );
};


export default SignupPage;
